import React from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash-es';
import { Button as RapydButton } from '@rapyd/react-ui-library';
import {
  ButtonProps,
  ButtonVariants
} from '@rapyd/react-ui-library/dist/components/Button';
import { ThemeBreakpoints, ThemeFamilies } from 'models';
import { isBelowMinContrast } from 'styles/mixins';

export { ButtonVariants };

export interface IButtonProps extends ButtonProps {
  fixedHeight?: boolean;
  overrideTextColorByContrast?: boolean;
  customStyleOverride?: boolean;
}

const getButtonByVariant = (variant: ButtonVariants) => {
  switch (variant) {
    case ButtonVariants.FILLED:
    case ButtonVariants.BORDERED: {
      return SquaredButton;
    }
    case ButtonVariants.OUTLINED: {
      return OutlinedButton;
    }
    case ButtonVariants.TEXT: {
      return TextButton;
    }
    default:
      return SquaredButton;
  }
};

const Button = ({
  className,
  name,
  children,
  staticSize = ThemeBreakpoints.LARGE,
  variant = ButtonVariants.BORDERED,
  themeFamily = ThemeFamilies.PRIMARY,
  isLoading,
  fixedHeight = true,
  overrideTextColorByContrast = true,
  customStyleOverride = false,
  ...rest
}: IButtonProps) => {
  const ButtonVariant = getButtonByVariant(variant);

  return (
    <ButtonVariant
      className={className}
      name={name || 'button'}
      variant={variant}
      staticSize={staticSize}
      isLoading={isLoading}
      fixedHeight={fixedHeight}
      themeFamily={themeFamily}
      overrideTextColorByContrast={overrideTextColorByContrast}
      customStyleOverride={customStyleOverride}
      {...rest}
    >
      {children}
    </ButtonVariant>
  );
};

export default Button;

export const getTextColorByContrast = (
  buttonProps: IButtonProps & { theme: Record<string, any> }
) => {
  const { theme, themeFamily, overrideTextColorByContrast, variant } = buttonProps || {};
  const shouldOverrideTextByContrast = overrideTextColorByContrast ?? true;

  if (!themeFamily || themeFamily !== ThemeFamilies.PRIMARY) return;
  if (!shouldOverrideTextByContrast) return;

  const backgroundColor = theme.families.button.primary.main;
  const belowMinContrast = isBelowMinContrast(backgroundColor);

  if (
    [ButtonVariants.FILLED, ButtonVariants.BORDERED].includes(variant as ButtonVariants)
  ) {
    return `color: ${belowMinContrast ? theme.colors.secondaryText : theme.colors.white}`;
  }
  return `color: ${belowMinContrast ? theme.colors.secondaryText : backgroundColor}`;
};

const TextColorSetter =
  () => (buttonProps: IButtonProps & { theme: Record<string, any> }) => {
    return css`
      ${getTextColorByContrast(buttonProps)}
    `;
  };

export const getCustomStyles =
  () => (buttonProps: IButtonProps & { theme: Record<string, any> }) => {
    const { theme } = buttonProps || {};

    const baseObject = theme?.hcp?.submit;
    if (isEmpty(baseObject)) return;

    return css`
      background-color: ${baseObject?.base?.backgroundColor};
      color: ${baseObject?.base?.color};
      font-weight: ${baseObject?.base?.fontWeight};
      font-size: ${baseObject?.base?.fontSize};
      font-family: ${baseObject?.base?.fontFamily};
      border: ${baseObject?.base?.border};
      border-radius: ${baseObject?.base?.borderRadius};
      box-shadow: ${baseObject?.base?.boxShadow};
      width: ${baseObject?.base?.width};
      height: ${baseObject?.base?.height};
      padding: ${baseObject?.base?.padding};
      cursor: ${baseObject?.base?.cursor};
      transition: ${baseObject?.transition};

      &:hover {
        background-color: ${baseObject?.hover?.backgroundColor};
        color: ${baseObject?.hover?.color};
        font-weight: ${baseObject?.hover?.fontWeight};
        border: ${baseObject?.hover?.border};
        border-radius: ${baseObject?.hover?.borderRadius};
        box-shadow: ${baseObject?.hover?.boxShadow};
      }

      &:active {
        background-color: ${baseObject?.active?.backgroundColor};
        color: ${baseObject?.active?.color};
      }
    `;
  };

const BaseButton = styled(RapydButton)<{
  fixedHeight: boolean;
  overrideTextColorByContrast: boolean;
  customStyleOverride: boolean;
}>`
  border-radius: 0.8rem;

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }

  ${({ fixedHeight }) =>
    fixedHeight &&
    css`
      height: 5rem;
    `}

  ${({ isLoading }) =>
    isLoading &&
    css`
      pointer-events: none;
    `}

  ${TextColorSetter()};

  ${({ customStyleOverride }) =>
    customStyleOverride &&
    css`
      ${getCustomStyles()};
    `};
`;

export const SquaredButtonCss = css`
  &:hover {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.2);
  }
  &:active {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.3);
  }
`;

const SquaredButton = styled(BaseButton)`
  ${SquaredButtonCss};
`;

export const TextCss = css`
  filter: contrast(1);
  &:hover {
    filter: contrast(0.7);
  }
`;

export const OutlinedButtonCss = css`
  ${TextCss};
`;

const OutlinedButton = styled(BaseButton)`
  ${OutlinedButtonCss};
`;

const TextButton = styled(BaseButton)`
  ${TextCss};
`;
